"use client";

// SKI BUTLERS WHISTLER (staging, founder brief 2026-10-21) — THE IN-PLACE "RENTAL GUESTS" SECTION OF CARD DETAILS.
//
// Rendered by InpageCardPayment only when the SERVER's quote named rented lines (`DesignQuote.rentals`); everything it asks for and
// every term it shows comes from that quote, never from the browser. Each guest is typed once and chosen on every item they use.
// The values live in the panel's React state only: never storage, a URL, an event, analytics or a log.

import type React from "react";
import { FieldError } from "@repo/ui/components/field";
import { FIELD_ERROR_COLOR, fieldMessageId } from "@repo/ui/lib/form-feedback";
import { RENTAL_ABILITIES, RENTAL_ABILITY_LABEL } from "@/lib/rental-terms";
import type { RentalAssignments, RentalGuestField, RentalPerson, RentalTerms } from "@/lib/rental-terms";

export const RENTAL_FIELD_LABEL: Record<RentalGuestField, string> = {
  name: "Full name",
  phone: "Phone",
  email: "Email",
  age: "Age",
  heightCm: "Height (cm)",
  weightKg: "Weight (kg)",
  shoeSize: "Shoe size",
  ability: "Ability",
};
const FIELDS = Object.keys(RENTAL_FIELD_LABEL) as RentalGuestField[];

/** The DOM id of a person's field, and of one unit's guest choice (also the keys of the panel's revealed/error maps). */
export const rentalFieldId = (personId: string, f: RentalGuestField) => `sg-rental-${personId}-${f}`;
export const rentalUnitId = (lineIndex: number, unit: number) => `sg-rental-unit-${lineIndex}-${unit}`;

const label: React.CSSProperties = { display: "block", marginBottom: 6, fontFamily: "var(--sg-font-secondary)", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--sg-grey-500)" };
const input: React.CSSProperties = { width: "100%", height: 42, padding: "0 12px", background: "#fff", border: "1px solid var(--sg-border-strong)", borderRadius: 10, fontFamily: "var(--sg-font-web)", fontSize: 14, color: "var(--sg-ink-900)" };
const h2: React.CSSProperties = { margin: 0, fontSize: 15, fontWeight: 700 };
const small: React.CSSProperties = { margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--sg-ink-900)" };

const day = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
const instant = (iso: string, timeZone: string) =>
  new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone });

/** The public terms, said once before Pay (they are the same policy on every rented line; dates are per line). */
function Terms({ rentals }: { rentals: Record<string, RentalTerms> }) {
  const all = Object.values(rentals);
  const first = all[0];
  if (!first) return null;
  const freeUntil = all.map((t) => t.cancellation.freeUntil).sort()[0] ?? first.cancellation.freeUntil;
  const tz = first.pickupWindow.timeZone;
  return (
    <div data-rental-terms style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <p style={small}>
        Delivered to you and picked up {day(first.pickupWindow.date)} between {first.pickupWindow.from} and {first.pickupWindow.to} (Whistler time). Booking questions:{" "}
        <a href={`mailto:${first.bookingContactEmail}`}>{first.bookingContactEmail}</a>.
      </p>
      <p style={small}>
        Free cancellation until {instant(freeUntil, tz)} (Whistler time). After that, a {first.cancellation.lateFeePercent}% fee applies to the rental. Rentals are booked at least {first.noticeHours} hours ahead.
      </p>
    </div>
  );
}

export function RentalGuests({
  rentals,
  lineNames,
  people,
  assignments,
  shown,
  locked,
  onPerson,
  onAssign,
  onAddPerson,
  onBlur,
}: {
  rentals: Record<string, RentalTerms>;
  lineNames: Record<string, string>;
  people: RentalPerson[];
  assignments: RentalAssignments;
  /** visible messages by field/unit id (already filtered by the panel's reveal rules) */
  shown: Record<string, string>;
  /** a draft order or an uncertain payment exists for these guests: they can no longer be changed on this page */
  locked: boolean;
  onPerson: (personId: string, field: RentalGuestField, value: string) => void;
  onAssign: (lineId: string, unit: number, personId: string) => void;
  onAddPerson: () => void;
  onBlur: (id: string) => void;
}) {
  const err = (id: string) => ({
    id,
    "aria-invalid": shown[id] ? true : undefined,
    "aria-describedby": shown[id] ? fieldMessageId(id) : undefined,
    onBlur: () => onBlur(id),
    disabled: locked,
    style: { ...input, border: `1px solid ${shown[id] ? FIELD_ERROR_COLOR : "var(--sg-border-strong)"}` },
  });
  return (
    <section data-rental-guests aria-labelledby="sg-rental-guests-h" style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4 }}>
      <h2 id="sg-rental-guests-h" style={h2}>
        Rental guests
      </h2>
      <Terms rentals={rentals} />
      <p style={small}>Enter each person once, then choose who uses each item. The rental team uses these details to fit your equipment.</p>
      {locked ? (
        <p data-rental-guests-locked role="note" style={{ ...small, fontWeight: 700 }}>
          These guest details are part of the order already started and can&apos;t be changed here.
        </p>
      ) : null}
      {people.map((p, n) => (
        <fieldset key={p.id} data-rental-person={p.id} style={{ border: "1px solid var(--sg-border)", borderRadius: 10, padding: 12, margin: 0, display: "flex", flexWrap: "wrap", gap: 10 }}>
          <legend style={{ fontSize: 13, fontWeight: 700, padding: "0 4px" }}>Guest {n + 1}</legend>
          {FIELDS.map((f) => {
            const id = rentalFieldId(p.id, f);
            return (
              <div key={f} style={{ flex: f === "name" || f === "email" ? "1 1 220px" : "1 1 120px", minWidth: 0 }}>
                <label style={{ display: "block" }}>
                  <span style={label}>{RENTAL_FIELD_LABEL[f]}</span>
                  {f === "ability" ? (
                    <select required value={p.ability} {...err(id)} onChange={(e) => onPerson(p.id, f, e.target.value)}>
                      <option value="">Choose a level…</option>
                      {RENTAL_ABILITIES.map((a) => (
                        <option key={a} value={a}>
                          {RENTAL_ABILITY_LABEL[a]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      required
                      value={p[f]}
                      {...err(id)}
                      onChange={(e) => onPerson(p.id, f, e.target.value)}
                      inputMode={f === "age" || f === "heightCm" || f === "weightKg" ? "numeric" : f === "phone" ? "tel" : undefined}
                      type={f === "email" ? "email" : f === "phone" ? "tel" : "text"}
                      autoComplete={n === 0 ? (f === "name" ? "name" : f === "email" ? "email" : f === "phone" ? "tel" : "off") : "off"}
                    />
                  )}
                </label>
                {shown[id] ? <FieldError role={undefined} id={fieldMessageId(id)}>{shown[id]}</FieldError> : null}
              </div>
            );
          })}
        </fieldset>
      ))}
      {!locked ? (
        <button type="button" data-rental-add-person onClick={onAddPerson} style={{ alignSelf: "flex-start", border: "1px solid var(--sg-border-strong)", background: "#fff", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Add another guest
        </button>
      ) : null}
      {Object.entries(rentals).map(([lineId, t], li) => {
        return (
          <div key={lineId} data-rental-line={lineId} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <p style={{ ...small, fontWeight: 700 }}>
              {lineNames[lineId] ?? "Rental item"} · {day(t.startDate)} to {day(t.endDate)} · {t.days} {t.days === 1 ? "day" : "days"}
            </p>
            {Array.from({ length: t.guestsRequired }, (_, unit) => {
              const id = rentalUnitId(li, unit);
              return (
                <div key={id} style={{ maxWidth: 360 }}>
                  <label style={{ display: "block" }}>
                    <span style={label}>{t.guestsRequired > 1 ? `Who uses item ${unit + 1}` : "Who uses this item"}</span>
                    <select required value={assignments[lineId]?.[unit] ?? ""} {...err(id)} onChange={(e) => onAssign(lineId, unit, e.target.value)}>
                      <option value="">Choose a guest…</option>
                      {people.map((p, n) => (
                        <option key={p.id} value={p.id}>
                          {p.name.trim() || `Guest ${n + 1}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  {shown[id] ? <FieldError role={undefined} id={fieldMessageId(id)}>{shown[id]}</FieldError> : null}
                </div>
              );
            })}
          </div>
        );
      })}
    </section>
  );
}
